import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { scaleQuantile } from "d3-scale";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MapPin } from "lucide-react";
import { api, fmtMoney, fmtMoneyShort, fmtNum } from "@/lib/api";

const US_TOPO =
  "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

// FIPS code -> 2-letter state code
const FIPS_TO_USPS = {
  "01": "AL", "02": "AK", "04": "AZ", "05": "AR", "06": "CA", "08": "CO",
  "09": "CT", "10": "DE", "11": "DC", "12": "FL", "13": "GA", "15": "HI",
  "16": "ID", "17": "IL", "18": "IN", "19": "IA", "20": "KS", "21": "KY",
  "22": "LA", "23": "ME", "24": "MD", "25": "MA", "26": "MI", "27": "MN",
  "28": "MS", "29": "MO", "30": "MT", "31": "NE", "32": "NV", "33": "NH",
  "34": "NJ", "35": "NM", "36": "NY", "37": "NC", "38": "ND", "39": "OH",
  "40": "OK", "41": "OR", "42": "PA", "44": "RI", "45": "SC", "46": "SD",
  "47": "TN", "48": "TX", "49": "UT", "50": "VT", "51": "VA", "53": "WA",
  "54": "WV", "55": "WI", "56": "WY",
};

const METRIC_OPTIONS = [
  { value: "gwp", label: "Reported GWP", money: true },
  { value: "policies", label: "Policy count", money: false },
  { value: "ar_outstanding", label: "AR outstanding", money: true },
  { value: "mga_net", label: "MGA net (prov)", money: true },
  { value: "broker_commission", label: "Broker commission", money: true },
];

// light -> dark blue scale (avoid purple slop)
const COLOR_RAMP = [
  "#eff6ff",
  "#dbeafe",
  "#bfdbfe",
  "#93c5fd",
  "#60a5fa",
  "#3b82f6",
  "#2563eb",
  "#1d4ed8",
  "#1e40af",
];

export default function StateMap() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [metric, setMetric] = useState("gwp");
  const [hover, setHover] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    api
      .get("/dashboard/state-map")
      .then((r) => setData(r.data))
      .catch(() => setData({ states: [], totals: {} }));
  }, []);

  const byCode = useMemo(() => {
    const m = new Map();
    for (const s of data?.states || []) m.set(s.state, s);
    return m;
  }, [data]);

  const metricMeta = METRIC_OPTIONS.find((m) => m.value === metric) || METRIC_OPTIONS[0];
  const values = useMemo(() => {
    return (data?.states || []).map((s) => s[metric] || 0).filter((v) => v > 0);
  }, [data, metric]);

  const colorScale = useMemo(() => {
    if (values.length === 0) return () => COLOR_RAMP[0];
    return scaleQuantile().domain(values).range(COLOR_RAMP);
  }, [values]);

  const top = useMemo(() => {
    return [...(data?.states || [])].sort((a, b) => (b[metric] || 0) - (a[metric] || 0)).slice(0, 8);
  }, [data, metric]);

  const fmt = (v) => (metricMeta.money ? fmtMoney(v) : fmtNum(v));
  const fmtShort = (v) => (metricMeta.money ? fmtMoneyShort(v) : fmtNum(v));

  function onStateClick(code) {
    navigate(`/policies?state=${encodeURIComponent(code)}`);
  }

  return (
    <Card className="p-6 border-slate-200" data-testid="state-map-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-400 font-medium">
            <MapPin className="w-3.5 h-3.5" strokeWidth={2} />
            Geographic concentration
          </div>
          <h2 className="text-base font-semibold tracking-tight mt-0.5">
            National distribution by state
          </h2>
          <p className="text-[12px] text-slate-500 mt-0.5">
            {data?.totals?.states_with_data ? `${data.totals.states_with_data} states active` : "Loading…"}
            {data?.totals?.policies != null && (
              <> • {fmtNum(data.totals.policies)} policies • Total GWP {fmtMoney(data.totals.gwp)}</>
            )}
          </p>
        </div>
        <Select value={metric} onValueChange={setMetric}>
          <SelectTrigger className="w-[200px] h-9" data-testid="state-map-metric">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {METRIC_OPTIONS.map((m) => (
              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map */}
        <div
          className="lg:col-span-2 relative bg-slate-50 rounded-lg border border-slate-200"
          data-testid="state-map-svg"
          onMouseMove={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
          }}
          onMouseLeave={() => setHover(null)}
        >
          <ComposableMap projection="geoAlbersUsa" width={780} height={450} style={{ width: "100%", height: "auto" }}>
            <Geographies geography={US_TOPO}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const fips = geo.id?.toString().padStart(2, "0");
                  const code = FIPS_TO_USPS[fips];
                  const row = code ? byCode.get(code) : null;
                  const val = row?.[metric] || 0;
                  const fill = val > 0 ? colorScale(val) : "#f1f5f9";
                  const isHover = hover?.code === code;
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      data-testid={`state-${code}`}
                      onMouseEnter={() => setHover({ code, row, name: geo.properties.name })}
                      onClick={() => code && row && onStateClick(code)}
                      style={{
                        default: {
                          fill,
                          stroke: "#fff",
                          strokeWidth: 0.75,
                          outline: "none",
                          cursor: row ? "pointer" : "default",
                          transition: "fill 120ms",
                        },
                        hover: {
                          fill: row ? "#1e3a8a" : fill,
                          stroke: "#fff",
                          strokeWidth: 1,
                          outline: "none",
                          cursor: row ? "pointer" : "default",
                        },
                        pressed: { fill: "#1e40af", outline: "none" },
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ComposableMap>

          {/* Tooltip */}
          {hover && (
            <div
              className="pointer-events-none absolute z-10 bg-slate-900 text-white text-[12px] rounded-md shadow-lg px-3 py-2 min-w-[160px]"
              style={{
                left: Math.min(tooltipPos.x + 12, 600),
                top: Math.max(tooltipPos.y - 12, 0),
              }}
              data-testid="state-map-tooltip"
            >
              <div className="font-semibold text-white">
                {hover.name} {hover.code && <span className="text-slate-400">({hover.code})</span>}
              </div>
              {hover.row ? (
                <>
                  <div className="mt-1 text-white/90">
                    {metricMeta.label}:{" "}
                    <span className="font-medium">{fmt(hover.row[metric])}</span>
                  </div>
                  <div className="text-[11px] text-white/70 mt-0.5">
                    {fmtNum(hover.row.policies)} policies • GWP {fmtMoneyShort(hover.row.gwp)}
                  </div>
                  <div className="text-[11px] text-blue-300 mt-1">Click to view →</div>
                </>
              ) : (
                <div className="text-[11px] text-slate-400 mt-1">No policies</div>
              )}
            </div>
          )}

          {/* Legend */}
          <div className="flex items-center gap-2 px-3 pb-3 pt-1 text-[10px] text-slate-500">
            <span>Less</span>
            <div className="flex h-2 flex-1 rounded overflow-hidden ring-1 ring-slate-200">
              {COLOR_RAMP.map((c) => (
                <div key={c} className="flex-1" style={{ background: c }} />
              ))}
            </div>
            <span>More</span>
          </div>
        </div>

        {/* Top states list */}
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium mb-3">
            Top 8 states
          </div>
          <ol className="space-y-2">
            {top.map((s, i) => (
              <li
                key={s.state}
                data-testid={`top-state-${s.state}`}
                onClick={() => onStateClick(s.state)}
                className="flex items-center gap-3 px-2.5 py-2 rounded-md hover:bg-slate-50 cursor-pointer transition"
              >
                <div className="w-5 text-[11px] text-slate-400 tabular-nums">{i + 1}</div>
                <div className="w-9 h-6 rounded bg-slate-100 text-[11px] font-semibold text-slate-700 flex items-center justify-center ring-1 ring-slate-200">
                  {s.state}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-medium text-slate-900 tabular-nums">
                    {fmtShort(s[metric])}
                  </div>
                  <div className="text-[11px] text-slate-500">{fmtNum(s.policies)} policies</div>
                </div>
              </li>
            ))}
            {top.length === 0 && (
              <li className="text-sm text-slate-400 text-center py-4">No data</li>
            )}
          </ol>
        </div>
      </div>
    </Card>
  );
}
